import React, { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { IconButton } from '../button';
import { DelIcon } from '../icons';
import { Caption } from '../caption';
import { useUniqID } from '../hooks';
import { imperativeChangeInputValue, mergeRefs } from '../utils';
import { InputLabel } from './input-label';
import styles from './styles/base.module.css';
/**
 * Компонент `<Input />` Текстовые поля позволяют пользователям вводить текст в пользовательский интерфейс.
 *
 * @example ``` tsx
 *  <Input onChange={changeValue} value={value}/>
 * ```
 */

export const Input = /*#__PURE__*/React.forwardRef(function Input(props, ref) {
  const {
    label,
    value = '',
    onBlur,
    mobile,
    onFocus,
    onChange,
    disabled,
    placeholder,
    startAdornment,
    endAdornment,
    className,
    error,
    allowClear = false,
    inputSize = 'l',
    caption,
    captionMessage,
    id,
    fullWidth,
    center,
    ...inputProps
  } = props;
  const inputID = useUniqID();
  const testId = props['data-testid'] || 'input';
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const inputRootClass = useMemo(() => clsx(styles.inputRoot, styles[inputSize], {
    [styles.hasLabel]: !!label,
    [styles.adornment]: !!startAdornment,
    [styles.error]: !!error,
    [styles.inputMobile]: mobile,
    [styles.disabled]: disabled,
    [styles.focused]: focused,
    [styles.active]: focused || value !== '',
    [styles.fullWidth]: fullWidth,
    [styles.withClear]: allowClear,
    [styles.center]: !!center
  }, className), [className, inputSize, error, mobile, disabled, focused, value, startAdornment, label, fullWidth, allowClear, center]);
  const inputFieldListClass = useMemo(() => clsx(styles.inputFieldList, {
    [styles.inputFieldListWithLabel]: !!label
  }), [label]);
  const onChangeInput = useCallback(e => onChange == null ? void 0 : onChange(e, e.target.value), [onChange]);
  const onClearInput = useCallback(e => {
    var _inputRef$current;

    imperativeChangeInputValue(inputRef, e, '');
    (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
  }, []);
  const onFocusInput = useCallback(e => {
    if (!focused) {
      setFocused(true);
      onFocus == null ? void 0 : onFocus(e);
    }
  }, [focused, onFocus]);
  const onBlurInput = useCallback(e => {
    // Дикий хак для того, чтобы с инпута не слетал фокус когда мы нажимаем на кнопку сбросить
    const relatedTarget = e.relatedTarget || e.nativeEvent.relatedTarget || e.nativeEvent.explicitOriginalTarget;
    const canReset = !(relatedTarget && inputRef.current && inputRef.current.parentElement && inputRef.current.parentElement.contains(relatedTarget));

    if (focused && canReset) {
      setFocused(false);
      onBlur == null ? void 0 : onBlur(e);
    }
  }, [focused, onBlur, inputRef]);
  return /*#__PURE__*/React.createElement("div", {
    className: inputRootClass,
    "data-testid": testId
  }, startAdornment && /*#__PURE__*/React.createElement("div", {
    className: styles.inputStartAdornment
  }, startAdornment), /*#__PURE__*/React.createElement("input", {
    id: id != null ? id : inputID,
    ref: mergeRefs([inputRef, ref]),
    className: styles.input,
    value: value,
    disabled: disabled,
    placeholder: placeholder,
    onBlur: onBlurInput,
    onFocus: onFocusInput,
    onChange: onChangeInput,
    ...inputProps,
    "data-testid": `${testId}-input`
  }), endAdornment && /*#__PURE__*/React.createElement("div", {
    className: styles.inputEndAdornment
  }, endAdornment), allowClear && value && /*#__PURE__*/React.createElement(IconButton, {
    size: inputSize === 's' ? 's' : 'm',
    className: styles.inputButton,
    label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0441\u043E\u0434\u0435\u0440\u0436\u0438\u043C\u043E\u0435 \u043F\u043E\u043B\u044F",
    onClick: onClearInput,
    disabled: disabled,
    "data-testid": `${testId}-clear`
  }, /*#__PURE__*/React.createElement(DelIcon, null)), /*#__PURE__*/React.createElement("fieldset", {
    className: inputFieldListClass
  }, /*#__PURE__*/React.createElement(InputLabel, {
    label: label
  })), label && /*#__PURE__*/React.createElement("label", {
    className: styles.inputLabel,
    htmlFor: id != null ? id : inputID,
    "data-testid": `${testId}-label`
  }, label), captionMessage ? /*#__PURE__*/React.createElement(Caption, {
    error: error,
    className: styles.caption,
    message: captionMessage
  }) : null, caption && /*#__PURE__*/React.createElement("div", {
    className: styles.caption
  }, caption));
});